import { getCategoryCounts, getSearchSuggestions } from '../util/api';

/**
 * The topbar's browsing data, on every page: the listing count of each category (the
 * browse menu) and the listings suggested for the words being typed in the search box.
 */

// ================ Action types ================ //

export const FETCH_CATEGORY_COUNTS_REQUEST = 'app/browse/FETCH_CATEGORY_COUNTS_REQUEST';
export const FETCH_CATEGORY_COUNTS_SUCCESS = 'app/browse/FETCH_CATEGORY_COUNTS_SUCCESS';
export const FETCH_CATEGORY_COUNTS_ERROR = 'app/browse/FETCH_CATEGORY_COUNTS_ERROR';

export const SEARCH_SUGGESTIONS_REQUEST = 'app/browse/SEARCH_SUGGESTIONS_REQUEST';
export const SEARCH_SUGGESTIONS_SUCCESS = 'app/browse/SEARCH_SUGGESTIONS_SUCCESS';

// Suggestions are asked for once typing pauses this long
export const SEARCH_SUGGESTIONS_DELAY_MS = 250;
// Fewer letters than this suggest nothing
export const SEARCH_SUGGESTIONS_MIN_LENGTH = 2;

// ================ Reducer ================ //

const initialState = {
  // { [categoryId]: count }, or null while unknown (the menu then shows no counts)
  categoryCounts: null,
  fetchCategoryCountsInProgress: false,
  // The words typed last, and the listings suggested for them ([] until they come)
  searchSuggestionsFor: '',
  searchSuggestions: [],
};

export default function browseReducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_CATEGORY_COUNTS_REQUEST:
      return { ...state, fetchCategoryCountsInProgress: true };
    case FETCH_CATEGORY_COUNTS_SUCCESS:
      return { ...state, fetchCategoryCountsInProgress: false, categoryCounts: payload };
    case FETCH_CATEGORY_COUNTS_ERROR:
      return { ...state, fetchCategoryCountsInProgress: false };

    case SEARCH_SUGGESTIONS_REQUEST:
      // The suggestions shown stay until the new ones come (no flicker while typing)
      return {
        ...state,
        searchSuggestionsFor: payload,
        searchSuggestions:
          payload.length < SEARCH_SUGGESTIONS_MIN_LENGTH ? [] : state.searchSuggestions,
      };
    case SEARCH_SUGGESTIONS_SUCCESS:
      // Only the answer for the words typed last: an earlier one arriving late is dropped
      return payload.keywords === state.searchSuggestionsFor
        ? { ...state, searchSuggestions: payload.suggestions }
        : state;

    default:
      return state;
  }
}

// ================ Thunks ================ //

/**
 * The listing count of each category, once per visit (the server caches them for
 * 15 minutes). Asked for as the browse menu opens; a failure leaves them unknown.
 */
export const fetchCategoryCounts = () => (dispatch, getState) => {
  const { categoryCounts, fetchCategoryCountsInProgress } = getState().browse;
  if (categoryCounts || fetchCategoryCountsInProgress) return Promise.resolve();
  dispatch({ type: FETCH_CATEGORY_COUNTS_REQUEST });
  return getCategoryCounts()
    .then(response => {
      const counts = response?.counts || null;
      dispatch(
        counts
          ? { type: FETCH_CATEGORY_COUNTS_SUCCESS, payload: counts }
          : { type: FETCH_CATEGORY_COUNTS_ERROR }
      );
    })
    .catch(() => dispatch({ type: FETCH_CATEGORY_COUNTS_ERROR }));
};

// The wait before the latest words are asked about: { timer, cancel }
let pendingSuggestions = null;

/**
 * Listings suggested for the words in the search box, asked for as they are typed:
 * once typing pauses (SEARCH_SUGGESTIONS_DELAY_MS), and only for the words typed last.
 * A failure suggests none, and the search box shows category suggestions only.
 */
export const suggestListings = words => dispatch => {
  const keywords = (words || '').trim();
  if (pendingSuggestions) {
    clearTimeout(pendingSuggestions.timer);
    pendingSuggestions.cancel();
    pendingSuggestions = null;
  }
  dispatch({ type: SEARCH_SUGGESTIONS_REQUEST, payload: keywords });
  if (keywords.length < SEARCH_SUGGESTIONS_MIN_LENGTH) return Promise.resolve();

  const typingPaused = new Promise(resolve => {
    pendingSuggestions = {
      timer: setTimeout(() => resolve(true), SEARCH_SUGGESTIONS_DELAY_MS),
      cancel: () => resolve(false),
    };
  });
  return typingPaused.then(paused => {
    if (!paused) return null;
    pendingSuggestions = null;
    return getSearchSuggestions(keywords)
      .then(response => response?.suggestions || [])
      .catch(() => [])
      .then(suggestions =>
        dispatch({ type: SEARCH_SUGGESTIONS_SUCCESS, payload: { keywords, suggestions } })
      );
  });
};
